import React, { useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Container,
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  InputAdornment,
  Stack,
  IconButton,
} from "@mui/material";
import {
  Lock as LockIcon,
  LockReset as LockResetIcon,
  Visibility,
  VisibilityOff,
  CheckCircle,
} from "@mui/icons-material";
import * as myApi from "../../components/Api";
import AuthBackgroundSlideshow from "../../components/AuthBackground/Slideshow";

/**
 * Reset Password Page with Material UI
 * Features:
 * - Clean, centered card layout
 * - Password visibility toggle
 * - Password strength indicator
 * - Confirmation validation
 */
export default function ResetPassword() {
  const navigate = useNavigate();
  const { code } = useParams();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [validationErrors, setValidationErrors] = useState({
    password: "",
    confirmPassword: "",
  });

  const [passwordStrength, setPasswordStrength] = useState({
    length: false,
    uppercase: false,
    number: false,
    special: false,
  });

  const checkPasswordStrength = (password) => {
    setPasswordStrength({
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    });
  };

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    checkPasswordStrength(newPassword);
    if (validationErrors.password) {
      const errors = validatePassword(newPassword);
      setValidationErrors((prev) => ({
        ...prev,
        password: errors.password,
      }));
    }
  };

  const validatePassword = (password) => {
    const errors = {};
    if (!password || password.trim() === "") {
      errors.password = "Password is required";
    } else if (password.length < 6) {
      errors.password = "Password must be at least 6 characters";
    }
    return errors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    const formData = new FormData(event.currentTarget);
    const password = formData.get("password");
    const confirmPassword = formData.get("confirmPassword");

    // Validate passwords
    const errors = validatePassword(password);
    if (!confirmPassword || confirmPassword.trim() === "") {
      errors.confirmPassword = "Please confirm your password";
    } else if (password !== confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      toast("Please fix the validation errors", { type: "error" });
      setLoading(false);
      return;
    }

    const data = {
      data_email: formData.get("email"),
      data_kode: code,
      data_passwd: password,
    };

    try {
      const response = await myApi.post("/forgotpwd", data);

      if (response.data.status === "Email Exist") {
        setSuccess(true);
        toast("Password reset successful!", { type: "success" });
        setTimeout(() => {
          navigate("/auth/sign-in/");
        }, 2000);
      }
    } catch (error) {
      toast(error.response?.data?.message || "Failed to reset password", {
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const passwordScore = Object.values(passwordStrength).filter(Boolean).length;

  return (
    <>
      {/* Animated Background Slideshow */}
      <AuthBackgroundSlideshow />
      <Container maxWidth="sm" sx={{ minHeight: "100vh", py: 4 }}>
      <Paper
        elevation={0}
        sx={{
          p: 4,
          borderRadius: 3,
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        }}
      >
        {/* Icon */}
        <Box sx={{ mb: 3, textAlign: "center" }}>
          <LockResetIcon sx={{ fontSize: 64, color: "primary.main", mb: 2 }} />
        </Box>

        {/* Header */}
        <Typography variant="h4" component="h1" gutterBottom fontWeight={600}>
          Reset Password
        </Typography>
        <Typography variant="body1" color="text.secondary" mb={4}>
          Enter your new password below
        </Typography>

        {/* Success Message */}
        {success && (
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <CheckCircle sx={{ fontSize: 48, color: "success.main", mb: 2 }} />
            <Typography variant="h6" color="success.main" gutterBottom>
              Password Reset Successful!
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Redirecting to sign in...
            </Typography>
          </Box>
        )}

        {/* Form */}
        {!success && (
          <form onSubmit={handleSubmit}>
            <Stack spacing={3}>
              <TextField
                fullWidth
                required
                label="Email Address"
                name="email"
                type="email"
                placeholder="email@example.com"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      LockIcon
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                required
                label="New Password"
                name="password"
                type={showPassword ? "text" : "password"}
                placeholder="Min 6 characters"
                error={!!validationErrors.password}
                helperText={validationErrors.password}
                onChange={handlePasswordChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* Password Strength */}
              <Box sx={{ mt: -2 }}>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  Password Requirements:
                </Typography>
                <Stack direction="row" spacing={2} flexWrap="wrap">
                  <Typography
                    variant="caption"
                    color={passwordStrength.length ? "success.main" : "text.secondary"}
                  >
                    {passwordStrength.length ? "✓" : "○"} Min 8 characters
                  </Typography>
                  <Typography
                    variant="caption"
                    color={passwordStrength.uppercase ? "success.main" : "text.secondary"}
                  >
                    {passwordStrength.uppercase ? "✓" : "○"} 1 uppercase
                  </Typography>
                  <Typography
                    variant="caption"
                    color={passwordStrength.number ? "success.main" : "text.secondary"}
                  >
                    {passwordStrength.number ? "✓" : "○"} 1 number
                  </Typography>
                </Stack>
              </Box>

              <TextField
                fullWidth
                required
                label="Confirm Password"
                name="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Re-enter password"
                error={!!validationErrors.confirmPassword}
                helperText={validationErrors.confirmPassword}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                disabled={loading}
                startIcon={<LockResetIcon />}
                sx={{
                  py: 1.5,
                  fontSize: "1rem",
                  fontWeight: 600,
                  mt: 1,
                }}
              >
                {loading ? "Resetting..." : "Reset Password"}
              </Button>

              <Button
                component={Link}
                to="/auth/sign-in/"
                variant="text"
                fullWidth
                sx={{
                  textTransform: "none",
                  fontWeight: 500,
                }}
              >
                Back to Sign In
              </Button>
            </Stack>
          </form>
        )}

        {/* Footer */}
        <Box
          sx={{
            mt: 4,
            pt: 3,
            borderTop: 1,
            borderColor: "divider",
            textAlign: "center",
          }}
        >
          <Typography variant="caption" color="text.secondary">
            © 2026 KARSA NUSA. All rights reserved.
          </Typography>
        </Box>
      </Paper>
    </Container>
    </>
  );
}
